import { useState, useEffect, useCallback } from "react";
import { MdDownloadForOffline } from "react-icons/md";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { client, urlFor } from "../client";
import MasonryLayout from "./MasonryLayout";
import { pinDetailQuery, pinDetailMorePinQuery } from "../utils/GROQ-data";
import Spinner from "./Spinner";

const PinDetail = ({ user }) => {
  const [pins, setPins] = useState(null);
  const [pinDetail, setPinDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const { pinId } = useParams();

  const addComment = () => {
    if (comment) {
      setAddingComment(true);

      client
        .patch(pinId)
        .setIfMissing({ comments: [] })
        .insert("after", "comments[-1]", [
          {
            comment,
            _key: uuidv4(),
            postedBy: {
              _type: "postedBy",
              _ref: user._id,
            },
          },
        ])
        .commit()
        .then(() => {
          fetchPinDetails();
          setComment("");
          setAddingComment(false);
        });
    }
  };

  const fetchPinDetails = useCallback(() => {
    let query = pinDetailQuery(pinId);

    if (query) {
      client.fetch(query).then((data) => {
        setPinDetail(data[0]);

        if (data[0]) {
          query = pinDetailMorePinQuery(data[0]);

          client.fetch(query).then((res) => setPins(res));
        }
      });
    }
  }, [pinId]);

  useEffect(() => {
    fetchPinDetails();
  }, [pinId, fetchPinDetails]);

  if (!pinDetail) return <Spinner message="Buzzing in progress..." />;

  return (
    <>
      <div
        className="flex flex-col m-auto bg-white shadow-md rounded-2xl xl-flex-row"
        style={{ maxWidth: "1200px" }}
      >
        <div className="flex items-center justify-center flex-initial mx-3 overflow-hidden md:items-start">
          <img
            src={pinDetail?.image && urlFor(pinDetail.image).url()}
            className="object-scale-down h-auto max-w-full mt-4 max-h-96 rounded-2xl"
            alt="user-post"
          />
        </div>
        <div className="flex-1 w-full p-5 xl:min-w-620">
          <div className="flex items-center gap-2 justify-evenly">
            <div className="flex items-center gap-2">
              <a
                href={`${pinDetail.image?.asset?.url}?dl=`}
                download
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center gap-2 p-2 text-lg rounded-full outline-none opacity-75 bg-amber-400 text-dark hover:opacity-100 hover:shadow-md"
              >
                <MdDownloadForOffline />
                Download
              </a>
            </div>
            <div className="w-40 p-2 truncate rounded-full opacity-75 bg-amber-400 hover:opacity-100 hover:shadow-md">
              <a href={pinDetail.destination} target="_blank" rel="noreferrer">
                {pinDetail.destination}
              </a>
            </div>
          </div>
          <div>
            <h1 className="mt-3 text-4xl font-bold break-words">
              {pinDetail.title}
            </h1>
            <p className="mt-3">{pinDetail.about}</p>
          </div>
          <div className="flex items-center gap-2 mt-5 bg-white rounded-lg">
            <img
              className="object-cover w-8 h-8 rounded-full"
              src={pinDetail.postedBy.image}
              alt="profile"
              referrerPolicy="no-referrer"
            />
            <p className="font-semibold capitalize">
              {pinDetail.postedBy.username}
            </p>
          </div>
          <h2 className="mt-5 text-2xl">Comments</h2>
          <div className="overflow-y-auto max-h-370">
            {pinDetail?.comments?.map((comment, i) => (
              <div
                className="flex items-center gap-2 mt-5 bg-white rounded-lg"
                key={i}
              >
                <img
                  src={comment.postedBy.image}
                  alt="profile"
                  className="w-10 h-10 rounded-full"
                />
                <div className="flex flex-col">
                  <p className="font-bold">{comment.postedBy.username}</p>
                  <p>{comment.comment}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <img
              className="w-10 h-10 rounded-full"
              src={user.image}
              alt="profile"
              referrerPolicy="no-referrer"
            />
            <input
              className="flex-1 p-2 border-2 border-gray-100 outline-none rounded-2xl focus:border-gray-300"
              type="text"
              placeholder="Add a comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <button
              type="button"
              className="px-6 py-2 text-base font-semibold text-black bg-yellow-500 rounded-full outline-none"
              onClick={addComment}
            >
              {addingComment ? "Posting comment..." : "Post"}
            </button>
          </div>
        </div>
      </div>
      {pins?.length > 0 ? (
        <>
          <h2 className="mt-8 mb-4 font-bold text-center text-2x">
            More like this
          </h2>
          <MasonryLayout pins={pins} />
        </>
      ) : (
        <Spinner message="Loading more pins..." />
      )}
    </>
  );
};

export default PinDetail;
